import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder, FolderPlus, FileText, Image, Video, Upload, Trash2, Edit2,
  ChevronRight, ChevronDown, Plus, X, ExternalLink, Download, Eye,
  Megaphone, Package, BookOpen, PlayCircle, Share2, FileImage
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './AdminResources.css';

interface ResourceFolder {
  id: number;
  name: string;
  description: string;
  slug: string;
  parent_id: number | null;
  icon: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  resource_count: number;
  total_resource_count: number;
  visibility: {
    retail_partner: boolean;
    wholesale_farm: boolean;
    bridge_club: boolean;
    store_club: boolean;
    admin: boolean;
  };
  visibility_list: string[];
  subfolders?: ResourceFolder[];
  breadcrumbs?: { id: number; name: string; slug: string }[];
}

interface Resource {
  id: number;
  title: string;
  description: string;
  resource_type: string;
  folder_id: number;
  folder_name: string;
  file_url: string | null;
  file_name: string;
  file_size: number | null;
  file_size_formatted: string;
  file_type: string;
  file_extension: string;
  youtube_url: string | null;
  youtube_video_id: string | null;
  is_video: boolean;
  thumbnail_url: string | null;
  override_visibility: boolean;
  visibility: {
    retail_partner: boolean;
    wholesale_farm: boolean;
    bridge_club: boolean;
    store_club: boolean;
    admin: boolean;
  };
  effective_visibility: Record<string, boolean>;
  sort_order: number;
  is_featured: boolean;
  is_active: boolean;
  download_count: number;
  created_at: string;
}

const RESOURCE_TYPES = [
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'image', label: 'Image', icon: Image },
  { value: 'logo', label: 'Logo', icon: FileImage },
  { value: 'flyer', label: 'Flyer/Ad', icon: Megaphone },
  { value: 'social_post', label: 'Social Media Post', icon: Share2 },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'feeding_guide', label: 'Feeding Guide', icon: BookOpen },
  { value: 'product_sheet', label: 'Product Sheet', icon: Package },
  { value: 'other', label: 'Other', icon: FileText },
];

const FOLDER_ICONS = [
  { value: 'folder', label: 'Folder', icon: Folder },
  { value: 'megaphone', label: 'Marketing', icon: Megaphone },
  { value: 'image', label: 'Images', icon: Image },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'book-open', label: 'Guides', icon: BookOpen },
  { value: 'package', label: 'Products', icon: Package },
  { value: 'share-2', label: 'Social', icon: Share2 },
  { value: 'play-circle', label: 'Tutorials', icon: PlayCircle },
];

const FOLDER_COLORS = [
  { value: 'gray', label: 'Gray', hex: '#6b7280' },
  { value: 'blue', label: 'Blue', hex: '#3b82f6' },
  { value: 'green', label: 'Green', hex: '#22c55e' },
  { value: 'yellow', label: 'Yellow', hex: '#eab308' },
  { value: 'red', label: 'Red', hex: '#ef4444' },
  { value: 'purple', label: 'Purple', hex: '#a855f7' },
  { value: 'orange', label: 'Orange', hex: '#f97316' },
];

const AdminResources: React.FC = () => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [allFolders, setAllFolders] = useState<ResourceFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<ResourceFolder | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());

  // Modal states
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ResourceFolder | null>(null);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'folder' | 'resource'; item: any } | null>(null);

  // Form states
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    parent_id: null as number | null,
    icon: 'folder',
    color: 'gray',
    visible_retail_partner: false,
    visible_wholesale_farm: false,
    visible_bridge_club: false,
    visible_store_club: false,
    visible_admin: false,
  });

  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    resource_type: 'document',
    file: null as File | null,
    youtube_url: '',
    is_featured: false,
    override_visibility: false,
    visible_retail_partner: false,
    visible_wholesale_farm: false,
    visible_bridge_club: false,
    visible_store_club: false,
    visible_admin: false,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Load folders
  const loadFolders = useCallback(async () => {
    try {
      const [treeRes, flatRes] = await Promise.all([
        api.get('/admin/resource-folders/'),
        api.get('/admin/resource-folders/?flat=true'),
      ]);
      setFolders(treeRes.data.folders);
      setAllFolders(flatRes.data.folders);
    } catch (err) {
      console.error('Error loading folders:', err);
    }
  }, []);

  // Load resources for selected folder
  const loadResources = useCallback(async (folderId: number) => {
    try {
      const res = await api.get(`/admin/resources/?folder=${folderId}`);
      setResources(res.data.resources);
    } catch (err) {
      console.error('Error loading resources:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadFolders();
      setLoading(false);
    };
    init();
  }, [loadFolders]);

  useEffect(() => {
    if (selectedFolder) {
      loadResources(selectedFolder.id);
    } else {
      setResources([]);
    }
  }, [selectedFolder, loadResources]);

  // Toggle folder expansion
  const toggleFolder = (folderId: number) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Get folder icon component
  const getFolderIcon = (iconName: string) => {
    const found = FOLDER_ICONS.find(i => i.value === iconName);
    return found ? found.icon : Folder;
  };

  // Get folder color hex
  const getFolderColor = (colorName: string) => {
    const found = FOLDER_COLORS.find(c => c.value === colorName);
    return found ? found.hex : '#6b7280';
  };

  // Get resource type icon
  const getResourceIcon = (type: string) => {
    const found = RESOURCE_TYPES.find(t => t.value === type);
    return found ? found.icon : FileText;
  };

  // Check if file is an image based on extension
  const isImageFile = (extension: string | null | undefined): boolean => {
    if (!extension) return false;
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
    return imageExtensions.includes(extension.toLowerCase());
  };

  // Get thumbnail URL for a resource (use file_url for images if no thumbnail)
  const getThumbnailUrl = (resource: Resource): string | null => {
    if (resource.thumbnail_url) return resource.thumbnail_url;
    if (isImageFile(resource.file_extension) && resource.file_url) {
      return resource.file_url;
    }
    return null;
  };

  // Render folder tree item
  const renderFolderItem = (folder: ResourceFolder, level = 0) => {
    const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolder?.id === folder.id;
    const IconComponent = getFolderIcon(folder.icon);
    const color = getFolderColor(folder.color);

    return (
      <div key={folder.id}>
        <div
          className={`folder-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => setSelectedFolder(folder)}
        >
          {hasSubfolders ? (
            <button
              className="folder-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="folder-expand-spacer" />
          )}
          <IconComponent size={16} style={{ color }} />
          <span className="folder-name">{folder.name}</span>
          {folder.resource_count > 0 && (
            <span className="folder-count">{folder.resource_count}</span>
          )}
        </div>
        {hasSubfolders && isExpanded && (
          <div className="folder-children">
            {folder.subfolders!.map(sf => renderFolderItem(sf, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Open folder modal
  const openFolderModal = (folder?: ResourceFolder) => {
    if (folder) {
      setEditingFolder(folder);
      setFolderForm({
        name: folder.name,
        description: folder.description,
        parent_id: folder.parent_id,
        icon: folder.icon,
        color: folder.color,
        visible_retail_partner: folder.visibility.retail_partner,
        visible_wholesale_farm: folder.visibility.wholesale_farm,
        visible_bridge_club: folder.visibility.bridge_club,
        visible_store_club: folder.visibility.store_club,
        visible_admin: folder.visibility.admin,
      });
    } else {
      setEditingFolder(null);
      setFolderForm({
        name: '',
        description: '',
        parent_id: selectedFolder?.id || null,
        icon: 'folder',
        color: 'gray',
        visible_retail_partner: false,
        visible_wholesale_farm: false,
        visible_bridge_club: false,
        visible_store_club: false,
        visible_admin: false,
      });
    }
    setShowFolderModal(true);
    setError('');
  };

  // Save folder
  const saveFolder = async () => {
    if (!folderForm.name.trim()) {
      setError('Folder name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingFolder) {
        await api.put(`/admin/resource-folders/${editingFolder.id}/`, folderForm);
      } else {
        await api.post('/admin/resource-folders/', folderForm);
      }
      await loadFolders();
      setShowFolderModal(false);
      if (selectedFolder && editingFolder?.id === selectedFolder.id) {
        // Refresh selected folder
        const res = await api.get(`/admin/resource-folders/${selectedFolder.id}/`);
        setSelectedFolder(res.data.folder);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save folder');
    } finally {
      setSaving(false);
    }
  };

  // Open resource modal
  const openResourceModal = (resource?: Resource, isVideo = false) => {
    if (resource) {
      setEditingResource(resource);
      setResourceForm({
        title: resource.title,
        description: resource.description,
        resource_type: resource.resource_type,
        file: null,
        youtube_url: resource.youtube_url || '',
        is_featured: resource.is_featured,
        override_visibility: resource.override_visibility,
        visible_retail_partner: resource.visibility.retail_partner,
        visible_wholesale_farm: resource.visibility.wholesale_farm,
        visible_bridge_club: resource.visibility.bridge_club,
        visible_store_club: resource.visibility.store_club,
        visible_admin: resource.visibility.admin,
      });
      setShowResourceModal(!resource.is_video);
      setShowVideoModal(resource.is_video);
    } else {
      setEditingResource(null);
      setResourceForm({
        title: '',
        description: '',
        resource_type: isVideo ? 'video' : 'document',
        file: null,
        youtube_url: '',
        is_featured: false,
        override_visibility: false,
        visible_retail_partner: false,
        visible_wholesale_farm: false,
        visible_bridge_club: false,
        visible_store_club: false,
        visible_admin: false,
      });
      if (isVideo) {
        setShowVideoModal(true);
      } else {
        setShowResourceModal(true);
      }
    }
    setError('');
  };

  // Save resource
  const saveResource = async () => {
    if (!resourceForm.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!selectedFolder) {
      setError('No folder selected');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('folder_id', selectedFolder.id.toString());
      formData.append('title', resourceForm.title);
      formData.append('description', resourceForm.description);
      formData.append('resource_type', resourceForm.resource_type);
      formData.append('is_featured', resourceForm.is_featured.toString());
      formData.append('override_visibility', resourceForm.override_visibility.toString());

      if (resourceForm.override_visibility) {
        formData.append('visible_retail_partner', resourceForm.visible_retail_partner.toString());
        formData.append('visible_wholesale_farm', resourceForm.visible_wholesale_farm.toString());
        formData.append('visible_bridge_club', resourceForm.visible_bridge_club.toString());
        formData.append('visible_store_club', resourceForm.visible_store_club.toString());
        formData.append('visible_admin', resourceForm.visible_admin.toString());
      }

      if (resourceForm.file) {
        formData.append('file', resourceForm.file);
      }

      if (resourceForm.youtube_url) {
        formData.append('youtube_url', resourceForm.youtube_url);
      }

      if (editingResource) {
        await api.put(`/admin/resources/${editingResource.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/admin/resources/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      await loadResources(selectedFolder.id);
      await loadFolders();
      setShowResourceModal(false);
      setShowVideoModal(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  // Delete folder or resource
  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setSaving(true);
    try {
      if (deleteTarget.type === 'folder') {
        await api.delete(`/admin/resource-folders/${deleteTarget.item.id}/`);
        if (selectedFolder?.id === deleteTarget.item.id) {
          setSelectedFolder(null);
        }
        await loadFolders();
      } else {
        await api.delete(`/admin/resources/${deleteTarget.item.id}/`);
        if (selectedFolder) {
          await loadResources(selectedFolder.id);
          await loadFolders();
        }
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // Extract YouTube video ID for preview
  const extractYouTubeId = (url: string) => {
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?]+)/,
      /youtube\.com\/embed\/([^?]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  if (loading) {
    return (
      <div className="admin-resources">
        <div className="loading-state">Loading resources...</div>
      </div>
    );
  }

  return (
    <div className="admin-resources">
      <div className="resources-header">
        <h1>Resources</h1>
        <p>Manage marketing materials, videos, and documents for partners and members</p>
      </div>

      <div className="resources-layout">
        {/* Left Panel - Folder Tree */}
        <div className="folders-panel">
          <div className="panel-header">
            <h2>Folders</h2>
            <button className="btn btn-sm btn-primary" onClick={() => openFolderModal()}>
              <FolderPlus size={14} />
              New Folder
            </button>
          </div>
          <div className="folder-tree">
            {folders.length === 0 ? (
              <div className="empty-state">
                <Folder size={32} />
                <p>No folders yet</p>
                <button className="btn btn-primary" onClick={() => openFolderModal()}>
                  Create First Folder
                </button>
              </div>
            ) : (
              folders.map(folder => renderFolderItem(folder))
            )}
          </div>
        </div>

        {/* Right Panel - Folder Contents */}
        <div className="contents-panel">
          {selectedFolder ? (
            <>
              <div className="folder-header">
                <div className="folder-info">
                  <h2>{selectedFolder.name}</h2>
                  {selectedFolder.description && (
                    <p className="folder-description">{selectedFolder.description}</p>
                  )}
                  <div className="visibility-badges">
                    {selectedFolder.visibility_list.map(v => (
                      <span key={v} className="visibility-badge">{v}</span>
                    ))}
                  </div>
                </div>
                <div className="folder-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openFolderModal(selectedFolder)}
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setDeleteTarget({ type: 'folder', item: selectedFolder });
                      setShowDeleteModal(true);
                    }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>

              <div className="resources-toolbar">
                <button
                  className="btn btn-primary"
                  onClick={() => openResourceModal(undefined, false)}
                >
                  <Upload size={14} />
                  Add Resource
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => openResourceModal(undefined, true)}
                >
                  <Video size={14} />
                  Add Video
                </button>
              </div>

              <div className="resources-grid">
                {resources.length === 0 ? (
                  <div className="empty-state full-width">
                    <FileText size={32} />
                    <p>No resources in this folder</p>
                  </div>
                ) : (
                  resources.map(resource => {
                    const IconComponent = getResourceIcon(resource.resource_type);
                    const thumbnailUrl = getThumbnailUrl(resource);
                    return (
                      <div key={resource.id} className="resource-card">
                        <div className="resource-thumbnail">
                          {resource.is_video && thumbnailUrl ? (
                            <div className="video-thumbnail">
                              <img src={thumbnailUrl} alt={resource.title} />
                              <div className="play-overlay">
                                <PlayCircle size={32} />
                              </div>
                            </div>
                          ) : thumbnailUrl ? (
                            <img src={thumbnailUrl} alt={resource.title} className="resource-image-preview" />
                          ) : (
                            <div className="resource-icon-placeholder">
                              <IconComponent size={32} />
                            </div>
                          )}
                          {resource.is_featured && (
                            <span className="featured-badge">Featured</span>
                          )}
                          {isImageFile(resource.file_extension) && !resource.is_video && (
                            <span className="file-type-badge">{resource.file_extension?.toUpperCase()}</span>
                          )}
                        </div>
                        <div className="resource-info">
                          <h4>{resource.title}</h4>
                          <div className="resource-meta">
                            <span className="resource-type">
                              {RESOURCE_TYPES.find(t => t.value === resource.resource_type)?.label || resource.resource_type}
                            </span>
                            {resource.file_size_formatted && (
                              <span className="resource-size">{resource.file_size_formatted}</span>
                            )}
                          </div>
                          {resource.download_count > 0 && (
                            <span className="download-count">
                              <Download size={12} /> {resource.download_count} downloads
                            </span>
                          )}
                        </div>
                        <div className="resource-actions">
                          {resource.file_url && (
                            <a
                              href={resource.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-icon"
                              title="View/Download"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                          <button
                            className="btn btn-sm btn-icon"
                            onClick={() => openResourceModal(resource)}
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-icon btn-danger"
                            onClick={() => {
                              setDeleteTarget({ type: 'resource', item: resource });
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="no-folder-selected">
              <Folder size={48} />
              <h3>Select a folder</h3>
              <p>Choose a folder from the left panel to view and manage its resources</p>
            </div>
          )}
        </div>
      </div>

      {/* Folder Modal */}
      {showFolderModal && (
        <div className="modal-overlay" onClick={() => setShowFolderModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingFolder ? 'Edit Folder' : 'Create Folder'}</h3>
              <button className="modal-close" onClick={() => setShowFolderModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={e => setFolderForm({ ...folderForm, name: e.target.value })}
                  placeholder="Marketing Materials"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={folderForm.description}
                  onChange={e => setFolderForm({ ...folderForm, description: e.target.value })}
                  placeholder="What's in this folder?"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Parent Folder</label>
                <select
                  value={folderForm.parent_id || ''}
                  onChange={e => setFolderForm({
                    ...folderForm,
                    parent_id: e.target.value ? parseInt(e.target.value) : null
                  })}
                >
                  <option value="">None (Root)</option>
                  {allFolders
                    .filter(f => f.id !== editingFolder?.id)
                    .map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))
                  }
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Icon</label>
                  <select
                    value={folderForm.icon}
                    onChange={e => setFolderForm({ ...folderForm, icon: e.target.value })}
                  >
                    {FOLDER_ICONS.map(i => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <select
                    value={folderForm.color}
                    onChange={e => setFolderForm({ ...folderForm, color: e.target.value })}
                  >
                    {FOLDER_COLORS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h4>Who can see this folder?</h4>

                <div className="visibility-group">
                  <h5>Wholesale Customers:</h5>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={folderForm.visible_retail_partner}
                      onChange={e => setFolderForm({
                        ...folderForm,
                        visible_retail_partner: e.target.checked
                      })}
                    />
                    Retail Partners
                    <small>Stores that resell our feed</small>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={folderForm.visible_wholesale_farm}
                      onChange={e => setFolderForm({
                        ...folderForm,
                        visible_wholesale_farm: e.target.checked
                      })}
                    />
                    Wholesale Farms
                    <small>Direct farm customers</small>
                  </label>
                </div>

                <div className="visibility-group">
                  <h5>Loyalty Programs:</h5>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={folderForm.visible_bridge_club}
                      onChange={e => setFolderForm({
                        ...folderForm,
                        visible_bridge_club: e.target.checked
                      })}
                    />
                    Bridge Club Members
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={folderForm.visible_store_club}
                      onChange={e => setFolderForm({
                        ...folderForm,
                        visible_store_club: e.target.checked
                      })}
                    />
                    Store Club Members
                  </label>
                </div>

                <div className="visibility-group">
                  <h5>Internal:</h5>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={folderForm.visible_admin}
                      onChange={e => setFolderForm({
                        ...folderForm,
                        visible_admin: e.target.checked
                      })}
                    />
                    Admin/Staff
                  </label>
                </div>

                <div className="visibility-shortcuts">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setFolderForm({
                      ...folderForm,
                      visible_retail_partner: true,
                      visible_wholesale_farm: true,
                      visible_bridge_club: true,
                      visible_store_club: true,
                      visible_admin: true,
                    })}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => setFolderForm({
                      ...folderForm,
                      visible_retail_partner: false,
                      visible_wholesale_farm: false,
                      visible_bridge_club: false,
                      visible_store_club: false,
                      visible_admin: false,
                    })}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFolderModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveFolder} disabled={saving}>
                {saving ? 'Saving...' : 'Save Folder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resource Modal */}
      {showResourceModal && (
        <div className="modal-overlay" onClick={() => setShowResourceModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingResource ? 'Edit Resource' : 'Add Resource'}</h3>
              <button className="modal-close" onClick={() => setShowResourceModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={resourceForm.title}
                  onChange={e => setResourceForm({ ...resourceForm, title: e.target.value })}
                  placeholder="Red Bridge Logo - Full Color"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={resourceForm.description}
                  onChange={e => setResourceForm({ ...resourceForm, description: e.target.value })}
                  placeholder="Full color logo for print and web"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={resourceForm.resource_type}
                  onChange={e => setResourceForm({ ...resourceForm, resource_type: e.target.value })}
                >
                  {RESOURCE_TYPES.filter(t => t.value !== 'video').map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>File {editingResource?.file_url ? '(Replace)' : '*'}</label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="resource-file"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setResourceForm({ ...resourceForm, file });
                      }
                    }}
                  />
                  <label htmlFor="resource-file" className="file-upload-label">
                    <Upload size={24} />
                    <span>Drag & drop or click to browse</span>
                    <small>PDF, PNG, JPG, SVG, DOC, ZIP</small>
                  </label>
                </div>
                {resourceForm.file && (
                  <div className="selected-file">
                    Selected: {resourceForm.file.name} ({Math.round(resourceForm.file.size / 1024)} KB)
                  </div>
                )}
                {editingResource?.file_name && !resourceForm.file && (
                  <div className="current-file">
                    Current: {editingResource.file_name}
                  </div>
                )}
              </div>

              <label className="checkbox-label standalone">
                <input
                  type="checkbox"
                  checked={resourceForm.is_featured}
                  onChange={e => setResourceForm({ ...resourceForm, is_featured: e.target.checked })}
                />
                Featured (show in featured section)
              </label>

              <label className="checkbox-label standalone">
                <input
                  type="checkbox"
                  checked={resourceForm.override_visibility}
                  onChange={e => setResourceForm({ ...resourceForm, override_visibility: e.target.checked })}
                />
                Override folder visibility
              </label>

              {resourceForm.override_visibility && (
                <div className="form-section visibility-override">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_retail_partner}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_retail_partner: e.target.checked
                      })}
                    />
                    Retail Partners
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_wholesale_farm}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_wholesale_farm: e.target.checked
                      })}
                    />
                    Wholesale Farms
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_bridge_club}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_bridge_club: e.target.checked
                      })}
                    />
                    Bridge Club
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_store_club}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_store_club: e.target.checked
                      })}
                    />
                    Store Club
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_admin}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_admin: e.target.checked
                      })}
                    />
                    Admin
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowResourceModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveResource} disabled={saving}>
                {saving ? 'Saving...' : 'Save Resource'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="modal-overlay" onClick={() => setShowVideoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingResource ? 'Edit Video' : 'Add Video'}</h3>
              <button className="modal-close" onClick={() => setShowVideoModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={resourceForm.title}
                  onChange={e => setResourceForm({ ...resourceForm, title: e.target.value })}
                  placeholder="Getting Started with Your Portal"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={resourceForm.description}
                  onChange={e => setResourceForm({ ...resourceForm, description: e.target.value })}
                  placeholder="Quick tutorial on using the partner portal"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>YouTube URL *</label>
                <input
                  type="url"
                  value={resourceForm.youtube_url}
                  onChange={e => setResourceForm({ ...resourceForm, youtube_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=abc123"
                />
              </div>

              {resourceForm.youtube_url && extractYouTubeId(resourceForm.youtube_url) && (
                <div className="youtube-preview">
                  <img
                    src={`https://img.youtube.com/vi/${extractYouTubeId(resourceForm.youtube_url)}/mqdefault.jpg`}
                    alt="Video thumbnail"
                  />
                  <div className="play-overlay">
                    <PlayCircle size={48} />
                  </div>
                  <div className="video-id-check">
                    Video ID: {extractYouTubeId(resourceForm.youtube_url)} ✓
                  </div>
                </div>
              )}

              <label className="checkbox-label standalone">
                <input
                  type="checkbox"
                  checked={resourceForm.is_featured}
                  onChange={e => setResourceForm({ ...resourceForm, is_featured: e.target.checked })}
                />
                Featured
              </label>

              <label className="checkbox-label standalone">
                <input
                  type="checkbox"
                  checked={resourceForm.override_visibility}
                  onChange={e => setResourceForm({ ...resourceForm, override_visibility: e.target.checked })}
                />
                Override folder visibility
              </label>

              {resourceForm.override_visibility && (
                <div className="form-section visibility-override">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_retail_partner}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_retail_partner: e.target.checked
                      })}
                    />
                    Retail Partners
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_wholesale_farm}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_wholesale_farm: e.target.checked
                      })}
                    />
                    Wholesale Farms
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_bridge_club}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_bridge_club: e.target.checked
                      })}
                    />
                    Bridge Club
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_store_club}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_store_club: e.target.checked
                      })}
                    />
                    Store Club
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={resourceForm.visible_admin}
                      onChange={e => setResourceForm({
                        ...resourceForm,
                        visible_admin: e.target.checked
                      })}
                    />
                    Admin
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowVideoModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveResource} disabled={saving}>
                {saving ? 'Saving...' : 'Save Video'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {deleteTarget.type === 'folder' ? (
                <p>
                  Delete folder "<strong>{deleteTarget.item.name}</strong>" and all{' '}
                  {deleteTarget.item.total_resource_count} resources inside?
                </p>
              ) : (
                <p>
                  Delete "<strong>{deleteTarget.item.title}</strong>"? This cannot be undone.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResources;
